import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { 
  Shield, 
  Eye, 
  EyeOff, 
  LogIn,
  User,
  Lock,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

const Login = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginType, setLoginType] = useState('discord'); // 'discord' or 'admin'
  const { user, isAuthenticated, login } = useAuth();
  const { admin, isAuthenticated: isAdminAuthenticated, login: adminLogin } = useAdminAuth();
  const navigate = useNavigate();
  
  const redirect = searchParams.get('redirect') || '/dashboard';

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(redirect);
    } else if (isAdminAuthenticated && admin) {
      navigate(redirect);
    }
  }, [isAuthenticated, isAdminAuthenticated, user, admin, navigate, redirect]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDiscordLogin = async () => {
    try {
      // Get Discord OAuth URL with redirect parameter
      const response = await fetch(`/api/auth/discord/url?redirect=${encodeURIComponent(redirect)}`);
      const data = await response.json();
      
      if (data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        toast.error('Failed to get Discord login URL');
      }
    } catch (error) {
      console.error('Discord login error:', error);
      toast.error('Failed to initiate Discord login');
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Use unified auth system - login as regular user first
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        // Check if user is admin after successful login
        const userResponse = await fetch('/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${data.token}`
          }
        });

        if (userResponse.ok) {
          const userData = await userResponse.json();
          const user = userData.user;

          // Check if user has admin privileges
          if (user.isAdmin || ['admin', 'super_admin'].includes(user.role)) {
            // Use regular user login for unified auth system
            await login(data.token);
            
            // Also update AdminAuthContext
            adminLogin(data.token);
            
            toast.success('Admin access granted!');
            navigate(redirect);
          } else {
            toast.error('This account does not have admin privileges');
          }
        } else {
          toast.error('Failed to verify admin privileges');
        }
      } else {
        toast.error(data.error || 'Login failed');
      }
    } catch (error) {
      console.error('Admin login error:', error);
      toast.error('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-discord-darkest py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-discord-blurple rounded-full flex items-center justify-center">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-discord-lightest">
            Welcome to Discord Game Bot SaaS
          </h2>
          <p className="mt-2 text-sm text-discord-light">
            Choose your login method
          </p>
        </div>

        {/* Login Type Toggle */}
        <div className="flex rounded-lg bg-discord-dark p-1">
          <button
            type="button"
            onClick={() => setLoginType('discord')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              loginType === 'discord'
                ? 'bg-discord-blurple text-white'
                : 'text-discord-light hover:text-discord-lightest'
            }`}
          >
            Discord Login
          </button>
          <button
            type="button"
            onClick={() => setLoginType('admin')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              loginType === 'admin'
                ? 'bg-discord-blurple text-white'
                : 'text-discord-light hover:text-discord-lightest'
            }`}
          >
            Admin Login
          </button>
        </div>

        {/* Discord Login */}
        {loginType === 'discord' && (
          <div className="card">
            <div className="text-center">
              <div className="mx-auto h-12 w-12 bg-discord-blurple rounded-full flex items-center justify-center mb-4">
                <User className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-medium text-discord-lightest mb-2">
                Login with Discord
              </h3>
              <p className="text-sm text-discord-light mb-6">
                Use your Discord account to access the platform. Admin users with Discord access will automatically get admin privileges.
              </p>
              <button
                onClick={handleDiscordLogin}
                className="w-full btn btn-primary flex items-center justify-center space-x-2"
              >
                <LogIn className="h-4 w-4" />
                <span>Login with Discord</span>
              </button>
            </div>
          </div>
        )}

        {/* Admin Login */}
        {loginType === 'admin' && (
          <div className="card">
            <div className="text-center mb-6">
              <div className="mx-auto h-12 w-12 bg-discord-red rounded-full flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-medium text-discord-lightest mb-2">
                Admin Login
              </h3>
              <p className="text-sm text-discord-light">
                Access the admin panel with your credentials
              </p>
            </div>

            <form onSubmit={handleAdminLogin} className="space-y-6">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-discord-light mb-2">
                  Username or Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-discord-light" />
                  </div>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    required
                    value={formData.username}
                    onChange={handleChange}
                    className="input pl-10"
                    placeholder="Enter your username or email"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-discord-light mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-discord-light" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="input pl-10 pr-10"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-discord-light hover:text-discord-lightest" />
                    ) : (
                      <Eye className="h-5 w-5 text-discord-light hover:text-discord-lightest" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn btn-primary flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Logging in...</span>
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4" />
                    <span>Login as Admin</span>
                  </>
                )}
              </button>
            </form>

            {/* Admin Credentials Info */}
{/*            
            <div className="mt-6 p-4 bg-discord-dark rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-discord-yellow mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-discord-lightest mb-2">
                    Admin Access Methods
                  </h4>
                  <div className="text-sm text-discord-light space-y-2">
                    <div>
                      <p><strong>Form Login:</strong></p>
                      <p>Username: admin | Password: admin123</p>
                    </div>
                    <div>
                      <p><strong>Discord Login:</strong></p>
                      <p>Login with Discord account that has admin privileges</p>
                    </div>
                    <p className="text-xs text-discord-light/80 mt-2">
                      ⚠️ Both methods provide the same admin access level
                    </p>
                  </div>
                </div>
              </div>
            </div> */}



          </div>
        )}

        {/* Footer */}
        <div className="text-center">
          <p className="text-sm text-discord-light">
            Need help? Contact support at{' '}
            <a href="mailto:support@discordgamebot.com" className="text-discord-blurple hover:text-discord-blurple/80">
              support@discordgamebot.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
